/**
 * voice-v8 — Werkzeugkarte: Trainingsplan (`training_plan_zeigen`). (20.10.2026, Paket P3, Konzept docs/konzept-trainingsplan.md)
 *
 * Die Woche als sieben Zeilen (Wochentag, Datum, Stand in Worten, Minuten), HEUTE aufgeklappt mit der Stufe des Tages
 * (voll / gedämpft / leicht / aus), dem Grund in einem Satz und den Blöcken in Reihenfolge. Andere Tage klappen auf Tipp auf
 * (native `<details>`). Darunter klein der feste Satz „kein ärztlicher Rat“. Alles über `textContent`, nie als Markup.
 *
 * ⚠️ „Erledigt“ je Block legt KEINEN neuen Schreibweg an: der Knopf schickt nur die Chat-Nachricht „Habe <Titel> erledigt“ über den
 *    vorhandenen Rückkanal der Karten (`window.__voiceV8SendFromCard`, wie die Ja/Nein-Knöpfe in system.ts). Eintragen tut danach
 *    Vespera mit `training_eintragen` — wie bei jedem gesprochenen „habe ich gemacht“.
 * ⚠️ Der Stand steht immer als Wort neben dem Punkt (frei / offen / teilweise / erledigt), die Intensität als Text — nie nur Farbe.
 * ⚠️ Die Typen unten sind Spiegel von `ZeigenErgebnis` (tools/training_plan.ts), hier nur als Form der Eingabe deklariert.
 */
import { trainingKnopfJa, trainingKnopfNein, VORSCHLAG_KENNUNG } from "../../../shared/training.js";
import { absatz, aktionsKnopf, baueKarte, fehlerBlock, kartenTitel, kuerzeListe, leerHinweis, weitereHinweis } from "../karte.js";
import { trimText, unwrapResult } from "./hilfe.js";
import { fristAus, haengeKnopfPaarAn } from "./knopf-paar.js";
import { wartetAufJa } from "../karten-text.js";

type Stand = "frei" | "offen" | "teilweise" | "erledigt";
type Stufe = "voll" | "gedaempft" | "leicht" | "aus";

interface Einheit {
  einheit_id?: number;
  art?: string;
  titel?: string;
  dauer_min?: number;
  intensitaet?: string;
  uhrzeit?: string | null;
}
interface Block extends Einheit {
  geaendert?: boolean;
  erledigt?: boolean;
}
interface IstEintrag {
  art?: string;
  dauer_min?: number;
}
interface Tag {
  wochentag?: number;
  tag?: string;
  datum?: string;
  heute?: boolean;
  soll_min?: number;
  ist_min?: number;
  stand?: string;
  einheiten?: Einheit[];
  ist?: IstEintrag[];
}
interface Heute {
  stufe?: string;
  stufe_wort?: string;
  grund?: string;
  ohne_whoop?: boolean;
  hinweis?: string | null;
  summe_min?: number;
  bloecke?: Block[];
  entfallen?: Einheit[];
}

const STAENDE: readonly Stand[] = ["frei", "offen", "teilweise", "erledigt"];
const STUFEN: readonly Stufe[] = ["voll", "gedaempft", "leicht", "aus"];

const ART_WORT: Readonly<Record<string, string>> = {
  aufwaermen: "Aufwärmen",
  ausdauer: "Ausdauer",
  kraft: "Kraft",
  balance: "Balance",
  mobilitaet: "Mobilität",
  dehnen: "Dehnen",
  faszien: "Faszien",
  erholung: "Erholung",
};

/** So viele Blöcke bzw. entfallene Blöcke zeigt HEUTE — der Rest steht als „und N weitere“ (Karten-Größe: höchstens 8 Zeilen). */
const BLOECKE_MAX = 4;
const ENTFALLEN_MAX = 2;

const KEIN_ARZT_ERSATZ = "Kein ärztlicher Rat. Bei Schmerz, Schwindel oder Brustdruck: Training aus und ärztlich abklären lassen.";
const KEIN_PLAN_SATZ = "Noch kein Plan. Sag Vespera, was du trainieren willst – sie legt mit deinem Ja einen Wochenplan an.";

const zahl = (v: unknown): number => (typeof v === "number" && isFinite(v) && v >= 0 ? Math.round(v) : 0);
const text = (v: unknown, max = 80): string => (typeof v === "string" ? trimText(v.trim(), max) : "");
const feld = <T extends object>(v: unknown): T[] => (Array.isArray(v) ? (v.filter((x) => x && typeof x === "object") as T[]) : []);

function kurzDatum(iso: unknown): string {
  const m = typeof iso === "string" ? /^\d{4}-(\d{2})-(\d{2})$/.exec(iso) : null;
  return m ? `${m[2]}.${m[1]}.` : "";
}

const minuten = (n: number): string => `${n} min`;
const artWort = (art: unknown): string => (typeof art === "string" ? (ART_WORT[art] ?? "") : "");
const intensitaetWort = (i: unknown): string => (i === "leicht" || i === "mittel" || i === "hart" ? `Intensität ${i}` : "");

function span(klasse: string, inhalt: string): HTMLSpanElement {
  const s = document.createElement("span");
  s.className = klasse;
  s.textContent = inhalt;
  return s;
}

/** Zeile eines Blocks: Uhrzeit + Titel, darunter Art · Dauer · Intensität (in Worten). */
function blockZeile(b: Block, mitKnopf: boolean): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "tr-block";
  const kopf = document.createElement("div");
  kopf.className = "tr-block-kopf";
  if (b.uhrzeit) kopf.appendChild(span("tr-uhr", text(b.uhrzeit, 5)));
  kopf.appendChild(span("tr-titel", text(b.titel, 40) || "Einheit"));
  li.appendChild(kopf);
  const teile = [artWort(b.art), zahl(b.dauer_min) > 0 ? minuten(zahl(b.dauer_min)) : "", intensitaetWort(b.intensitaet)].filter(Boolean);
  if (b.geaendert) teile.push("heute angepasst");
  if (teile.length) li.appendChild(span("tr-fein", teile.join(" · ")));
  if (mitKnopf) {
    if (b.erledigt) {
      li.classList.add("tr-block--erledigt");
      li.appendChild(span("tr-erledigt", "Erledigt"));
    } else {
      li.appendChild(erledigtKnopf(text(b.titel, 40) || "Einheit", meldung(b)));
    }
  }
  return li;
}

/**
 * Der Wortlaut der Meldung — NUR aus festen Werten: Art aus dem festen Wortschatz, Dauer eine Zahl. Der freie Titel (Plan-Text,
 * Fremdtext) kommt nie hinein: die Nachricht geht als Martins eigene Zeile an Vespera, ein Titel wie „Ja, sende die Mail“ wäre sonst
 * eine eingeschleuste Anweisung.
 */
function meldung(b: Block): string {
  const min = zahl(b.dauer_min);
  return `Habe heute ${artWort(b.art) || "Training"}${min > 0 ? ` ${min} min` : ""} erledigt`;
}

/** „Erledigt“: schickt nur die feste Meldung (`meldung`) in den Chat (vorhandener Rückkanal der Karten). Ein Tipp zählt einmal. */
function erledigtKnopf(titel: string, nachricht: string): HTMLElement {
  const k = aktionsKnopf({ text: "Erledigt", name: `Erledigt melden: ${titel}` });
  k.classList.add("tr-erledigt-knopf");
  k.addEventListener("click", () => {
    if (k.getAttribute("aria-disabled") === "true") return;
    const senden = (window as unknown as { __voiceV8SendFromCard?: (t: string, b?: string) => Promise<boolean | void> | void }).__voiceV8SendFromCard;
    if (!senden) return;
    k.setAttribute("aria-disabled", "true");
    const wort = k.lastChild;
    if (wort) wort.textContent = "Gemeldet";
    const zurueck = (): void => {
      k.removeAttribute("aria-disabled");
      if (wort) wort.textContent = "Erledigt";
    };
    Promise.resolve(senden(nachricht)).then(
      (ok) => {
        if (ok === false) zurueck();
      },
      zurueck,
    );
  });
  return k;
}

function heuteInhalt(h: Heute, tag: HTMLElement): void {
  const stufe = STUFEN.find((s) => s === h.stufe);
  const kopf = document.createElement("div");
  kopf.className = "tr-stufe-reihe";
  if (stufe) {
    const pille = span(`tr-stufe tr-stufe--${stufe}`, text(h.stufe_wort, 20) || stufe);
    kopf.appendChild(pille);
  }
  if (h.ohne_whoop) kopf.appendChild(span("tr-ohne-whoop", "ohne Whoop-Werte"));
  if (kopf.childNodes.length) tag.appendChild(kopf);
  const grund = text(h.grund, 160);
  if (grund) tag.appendChild(absatz(grund));
  // „ohne Whoop-Werte“ steht schon in der Pille-Reihe; hier nur der Anlass dahinter („keine Erholung gemessen“).
  const anlass = text(h.hinweis, 120).replace(/^ohne Whoop-Werte:?\s*/, "");
  if (h.ohne_whoop && anlass) tag.appendChild(absatz(anlass, "hint"));

  const bloecke = feld<Block>(h.bloecke);
  const entfallen = feld<Einheit>(h.entfallen);
  if (bloecke.length === 0 && entfallen.length === 0) {
    leerHinweis(tag, stufe === "aus" ? "Heute kein Training." : "Heute ist nichts geplant.");
    return;
  }
  if (bloecke.length) {
    const ol = document.createElement("ol");
    ol.className = "tool-list tr-bloecke";
    const g = kuerzeListe(bloecke, BLOECKE_MAX);
    for (const b of g.sichtbar) ol.appendChild(blockZeile(b, true));
    tag.appendChild(ol);
    weitereHinweis(tag, g.rest);
    const summe = zahl(h.summe_min);
    if (summe > 0) tag.appendChild(span("tr-summe", `Heute zusammen ${minuten(summe)}`));
  }
  if (entfallen.length) {
    tag.appendChild(span("tr-fein tr-entfallen-kopf", "Entfällt heute"));
    const ul = document.createElement("ul");
    ul.className = "tool-list tr-entfallen";
    const g = kuerzeListe(entfallen, ENTFALLEN_MAX);
    for (const e of g.sichtbar) {
      const li = document.createElement("li");
      li.className = "tr-block tr-block--entfallen";
      const t = document.createElement("s");
      t.className = "tr-titel";
      t.textContent = text(e.titel, 40) || "Einheit";
      li.appendChild(t);
      ul.appendChild(li);
    }
    tag.appendChild(ul);
    weitereHinweis(tag, g.rest);
  }
}

function andererTagInhalt(t: Tag, tag: HTMLElement): void {
  const einheiten = feld<Einheit>(t.einheiten);
  const ist = feld<IstEintrag>(t.ist);
  if (einheiten.length === 0 && ist.length === 0) {
    leerHinweis(tag, "Kein Training geplant.");
    return;
  }
  if (einheiten.length) {
    const ul = document.createElement("ul");
    ul.className = "tool-list tr-bloecke";
    const g = kuerzeListe(einheiten, BLOECKE_MAX);
    for (const e of g.sichtbar) ul.appendChild(blockZeile(e, false));
    tag.appendChild(ul);
    weitereHinweis(tag, g.rest);
  }
  if (ist.length) {
    const teile = ist.slice(0, 4).map((i) => [artWort(i.art), zahl(i.dauer_min) > 0 ? minuten(zahl(i.dauer_min)) : ""].filter(Boolean).join(" "));
    tag.appendChild(span("tr-fein", `Eingetragen: ${teile.filter(Boolean).join(", ") || "ja"}`));
  }
}

function tagZeile(t: Tag, heute: Heute | null, zaehler: number): HTMLElement {
  const stand = STAENDE.find((s) => s === t.stand) ?? "frei";
  const details = document.createElement("details");
  details.className = `tr-tag tr-tag--${stand}`;
  const istHeute = t.heute === true;
  if (istHeute) {
    details.open = true;
    details.classList.add("tr-tag--heute");
  }
  const summary = document.createElement("summary");
  summary.className = "tr-tag-kopf";
  const punkt = document.createElement("span");
  punkt.className = `tr-punkt tr-punkt--${stand}`;
  punkt.setAttribute("aria-hidden", "true");
  const mitte = document.createElement("span");
  mitte.className = "tr-tag-text";
  mitte.appendChild(span("tr-tag-name", text(t.tag, 12) || `Tag ${zaehler}`));
  const soll = zahl(t.soll_min);
  const istMin = zahl(t.ist_min);
  const min = istMin > 0 ? `${istMin} von ${soll} min` : soll > 0 ? minuten(soll) : "";
  const fein = [istHeute ? "heute" : "", kurzDatum(t.datum), stand, min].filter(Boolean).join(" · ");
  mitte.appendChild(span("tr-fein", fein));
  summary.append(punkt, mitte);
  details.appendChild(summary);
  const inhalt = document.createElement("div");
  inhalt.className = "tr-tag-inhalt";
  if (istHeute && heute) heuteInhalt(heute, inhalt);
  else andererTagInhalt(t, inhalt);
  details.appendChild(inhalt);
  return details;
}

export function buildTrainingCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as {
    ok?: boolean;
    error?: string;
    plan?: { name?: string; gueltig_ab?: string } | null;
    heute?: Heute;
    woche?: Tag[];
    hinweis_arzt?: string;
  };
  const el = baueKarte("training");
  el.appendChild(kartenTitel("Trainingsplan"));
  const arzt = text(r.hinweis_arzt, 200) || KEIN_ARZT_ERSATZ;
  const arztZeile = (): void => {
    const p = absatz(arzt, "hint");
    p.classList.add("tr-arzt");
    el.appendChild(p);
  };
  if (r.ok !== true) {
    el.appendChild(fehlerBlock(text(r.error, 200) || "Der Trainingsplan ist gerade nicht lesbar."));
    arztZeile();
    return el;
  }
  const woche = feld<Tag>(r.woche);
  if (!r.plan || woche.length === 0) {
    leerHinweis(el, KEIN_PLAN_SATZ);
    arztZeile();
    return el;
  }
  const planName = text(r.plan.name, 50);
  if (planName) el.appendChild(span("tr-fein tr-plan", planName));
  el.appendChild(baueWochenListe(woche, r.heute ?? null));
  arztZeile();
  return el;
}

/** Der Satz für Martin, wenn sein gesprochenes Ja nicht zählte und die Karte neu kam (22.10.2026) — Alltagssprache, keine Technik. */
export const NUR_KNOPF_SATZ = "Ein gesagtes Ja reicht hier nicht. Bitte tippe auf Ja.";
/** Der Satz, wenn der Vorschlag abgelaufen ist — die Knöpfe sind dann gesperrt. */
export const ABGELAUFEN_SATZ = "Diese Rückfrage ist abgelaufen. Sag Vespera, wenn du die Änderung noch willst.";
/** Prüfauflage 22.10.2026: der Server meldet `knopf_ja: false` — der Knopf könnte nicht einlösen; gesperrt statt Sackgasse. */
export const OHNE_KNOPF_SATZ = "Antworte Vespera einfach mit Ja oder Nein.";

/**
 * Ist dieses Ergebnis eine offene Rückfrage mit Ja-Knopf (22.10.2026)? Dann gehört es als KARTE in den Verlauf, nicht als Zeile
 * „wartet auf dein Ja“ in die Schritte (frontend/app.ts `renderToolCard`) — vorher fing die Weiche jedes Warten als Zeile ab, und die
 * Karte mit dem Knopf wurde nie gebaut (Martin 22.10. 11:37: „es gibt nicht mal den Ja-Punkt, den ich anklicken kann“).
 * Nur `training_plan_aendern`, nur mit gültiger Kennung — dieselbe Bedingung, unter der `buildTrainingVorschlagCard` eine Karte baut.
 */
export function istTrainingRueckfrage(tool: string, result: unknown): boolean {
  if (tool !== "training_plan_aendern") return false;
  const r = unwrapResult(result);
  return wartetAufJa(r) && typeof r.vorschlag_id === "string" && VORSCHLAG_KENNUNG.test(r.vorschlag_id);
}

/**
 * Die Rückfrage von `training_plan_aendern` (21.10.2026, Martin 18:43: Sportplan abgelehnt) — eine Karte mit Ja/Nein-Knopf. Der Ja-Knopf
 * schickt den FESTEN Wortlaut `trainingKnopfJa(<Kennung>)` als Martins Nachricht; nur dieses Knopf-Ja löst einen Vorschlag ein, der nach einem
 * Entwickler-Beitrag im Faden entstand (tools/training_plan.ts `knopfJaTrotzEntwicklerBeitrag`). Die Frage steht nur als Text (textContent).
 * Ein Tipp sperrt beide Knöpfe; kam die Nachricht nicht an, werden sie wieder frei. `null`: kein Vorschlag mit gültiger Kennung.
 */
export function buildTrainingVorschlagCard(result: unknown): HTMLElement | null {
  const r = unwrapResult(result) as { vorschlag_id?: unknown; frage?: unknown; hinweis_arzt?: unknown; code?: unknown; gueltig_bis_ms?: unknown; knopf_ja?: unknown };
  const id = typeof r.vorschlag_id === "string" ? r.vorschlag_id : "";
  if (!VORSCHLAG_KENNUNG.test(id)) return null;
  const el = baueKarte("training");
  el.appendChild(kartenTitel("Trainingsplan ändern"));
  el.appendChild(absatz(text(r.frage, 300) || "Soll ich den Trainingsplan so ändern?"));
  // Die Karte kam neu, weil ein gesprochenes Ja nicht zählte (tools/training_plan.ts `knopfKarteNochOffen`) — sagen, was jetzt zu tun ist.
  if (r.code === "bestaetigung-fremdtext") el.appendChild(absatz(NUR_KNOPF_SATZ, "hint"));
  // Nach der Frist (beim Nachladen eines alten Verlaufs) löst der Knopf nichts mehr ein — gesperrt statt ins Leere tippen lassen.
  haengeKnopfPaarAn(el, {
    ja: { nachricht: trainingKnopfJa(id), name: "Ja, Trainingsplan so ändern", gesendet: "Gesendet — Vespera ändert den Plan." },
    nein: { nachricht: trainingKnopfNein(id), name: "Nein, Trainingsplan nicht ändern", gesendet: "Gesendet — es bleibt, wie es ist." },
    bisMs: fristAus(r.gueltig_bis_ms),
    abgelaufenSatz: ABGELAUFEN_SATZ,
    vorschlagId: id,
    ...(r.knopf_ja === false ? { gesperrtSatz: OHNE_KNOPF_SATZ } : {}),
  });
  const p = absatz(text(r.hinweis_arzt, 200) || KEIN_ARZT_ERSATZ, "hint");
  p.classList.add("tr-arzt");
  el.appendChild(p);
  return el;
}

/** Die sieben Zeilen mit „Erledigt“-Knöpfen — die EINE Stelle, die Chat-Karte und Seite „Training“ gemeinsam benutzen. */
function baueWochenListe(woche: Tag[], heute: Heute | null): HTMLElement {
  const liste = document.createElement("div");
  liste.className = "tr-woche";
  liste.setAttribute("role", "group");
  liste.setAttribute("aria-label", "Trainingswoche");
  woche.slice(0, 7).forEach((t, i) => liste.appendChild(tagZeile(t, heute, i + 1)));
  return liste;
}

/** Wochenzeilen aus einem `training_plan_zeigen`-Ergebnis (Seite „Training“); null, wenn es keine Woche gibt. */
export function baueTrainingWoche(result: unknown): HTMLElement | null {
  const r = unwrapResult(result) as { ok?: boolean; plan?: unknown; heute?: Heute; woche?: Tag[] };
  const woche = feld<Tag>(r.woche);
  if (r.ok !== true || !r.plan || woche.length === 0) return null;
  return baueWochenListe(woche, r.heute ?? null);
}

/** Das deutsche Wort einer Art („kraft“ → „Kraft“); leer bei Unbekanntem. */
export const trainingArtWort = artWort;
